import { ButtonProps } from '../../../Button';
import { CheckboxProps } from '../../../Checkbox';
import { HeadingProps } from '../../../Heading';
import { BadgeButtonProps } from '../../../BadgeButton';
import { MenuButtonProps } from '../../../MenuButton';
import { SelectProps } from '../../../Select';
import { LabelProps } from '../../../Label';
import { SegmentedButtonProps } from '../../../SegmentedButton';
import { ToggleButtonProps } from '../../../ToggleButton';
import { HelpPopupProps } from '../../../HelpPopup';
import { ImageProps } from '../../../Image';
import { BaseMenuButtonProps } from '../../../MenuButton/__internal__';
import { CustomToolbarItemProps } from '../..';
import { ComponentProps, Ref } from 'react';
import { ObjectMarkerProps } from '../../../ObjectMarker';
import { SplitButtonProps } from '../../../SplitButton';
import { SelectWithLabels } from './InnerToolbar';
import { OmitStrict } from '../../../../utility-types';
import { BaseLabelsProps } from '../../../withLabels';
import { UndoRedoProps } from '../../../UndoRedo';
import { ObjectSwitcherProps } from '../../../ObjectManager';
import { TestIdProps } from '../../../../util/testId';
import { PredefinedButtonProps, ToolbarSeparatorProps, BaseToolbarItem } from '../';

export interface InnerToolbarProps extends TestIdProps {
  items: InnerToolbarItem[];
  createSetRef?: (index: number) => (current: HTMLDivElement | null) => void;
}

// Steps to add a new component (XYZ) to the white-list of InnerToolbarItem
// 1. Create InnerToolbarXYZ in this file as follows
//    export interface InnerToolbarXYZ extends CustomToolbarItemProps, Omit<XYZProps, 'type'> {
//      type: 'XYZ';
//      xyzType: XYZProps['type']; // Only add this line if XYZProps['type'] exists
//    }
// 2. Union InnerToolbarXYZ to InnerToolbarItem
// 3. Add the type string literal 'XYZ' to InnerToolbarItemTypes
// 4. Add the type string literal 'XYZ' to FocusableItemTypes if that item is focusable
// 5. Create ToolbarXYZ in ToobarItem.ts as follows
//    export interface ToolbarXYZ extends ComponentOverflowConfig,
//        Omit<InnerToolbarXYZProps, keyof ComponentOverflowConfig> {
//          xyzOverflowPriority: InnerToolbarXYZProps['overflowPriority']; // Only add this line if XYZProps['overflowPriority'] exists
//          xyzMenuItemText: InnerToolbarXYZProps['menuItemText']; // Only add this line if XYZProps['menuItemText'] exists
//    }
// 6. Union InnerToolbarXYZ to ToolbarItem
// 7. Add mapping from XYZProps to XYZ in mapInnerToolbarItemToReactNode function in InnerToolbarItem.tsx
// 8. Add default overflow config for XYZ in getOverflowConfig function in Toolbar.tsx

export interface OmittedWithLabelsProps
  extends Pick<
    BaseLabelsProps,
    'labelPosition' | 'wrapLabel' | 'wrapDescription' | 'description'
  > {}

export interface InnerToolbarButton extends CustomToolbarItemProps, Omit<ButtonProps, 'type'> {
  type: 'Button';
  ref?: Ref<HTMLButtonElement>;
}

export interface InnerToolbarPredefinedButton
  extends CustomToolbarItemProps,
    Omit<PredefinedButtonProps, 'type'> {
  type: 'PredefinedButton';
  buttonType: PredefinedButtonProps['type'];
  ref?: Ref<HTMLButtonElement>;
}

export interface InnerToolbarCheckbox
  extends CustomToolbarItemProps,
    Omit<CheckboxProps, 'type' | 'wrapText'> {
  type: 'Checkbox';
  checked: boolean; // "mixed" state Checkbox is not allowed in Toolbar
  ref?: Ref<HTMLDivElement>;
}

export interface InnerToolbarHeading
  extends CustomToolbarItemProps,
    Omit<HeadingProps, 'type' | 'wrapText'> {
  type: 'Heading';
}

export interface InnerToolbarBadgeButton
  extends CustomToolbarItemProps,
    Omit<BadgeButtonProps, 'type'> {
  type: 'BadgeButton';
  ref?: Ref<HTMLButtonElement>;
}

export interface InnerToolbarMenuButton
  extends CustomToolbarItemProps,
    Omit<MenuButtonProps, 'type'> {
  type: 'MenuButton';
  ref?: Ref<HTMLButtonElement>;
}

export interface InnerToolbarSeparator
  extends CustomToolbarItemProps,
    Omit<ToolbarSeparatorProps, 'type'> {
  type: 'ToolbarSeparator';
}

export interface InnerToolbarDropdown extends CustomToolbarItemProps, Omit<SelectProps, 'type'> {
  type: 'Select';
  selectBoxRef?: Ref<HTMLDivElement>;
}

interface SelectWithLabelsProps
  extends OmitStrict<
    ComponentProps<typeof SelectWithLabels>,
    keyof OmittedWithLabelsProps | 'suffix'
  > {}

export interface InnerToolbarSelectWithLabels
  extends CustomToolbarItemProps,
    Omit<SelectWithLabelsProps, 'type'> {
  type: 'SelectWithLabels';
}

export interface InnerToolbarLabel
  extends CustomToolbarItemProps,
    // The label in Toolbar always has wrapText to be false and does not support having a helpPopup
    Omit<LabelProps, 'type' | 'wrapText' | 'helpPopup'> {
  type: 'Label';
  ref?: Ref<HTMLLabelElement>;
}

/* Internal Usage only */
export interface InnerToolbarImage extends CustomToolbarItemProps, Omit<ImageProps, 'type'> {
  type: 'Image';
}

/* Internal Usage only */
export interface InnerToolbarBaseMenuButton
  extends CustomToolbarItemProps,
    Omit<BaseMenuButtonProps, 'type'> {
  type: 'BaseMenuButton';
  ref?: Ref<HTMLButtonElement>;
}

export interface InnerToolbarSegmentedButton
  extends CustomToolbarItemProps,
    Omit<SegmentedButtonProps, 'type' | 'enableOverflow'> {
  type: 'SegmentedButton';
  focusedButtonRef?: Ref<HTMLButtonElement>;
}

export interface InnerToolbarToggleButton
  extends CustomToolbarItemProps,
    Omit<ToggleButtonProps, 'type'> {
  type: 'ToggleButton';
  ref?: Ref<HTMLButtonElement>;
  /**
   * @internal used by MessageSummary
   */
  textToDrop?: string;
}

// HelpPoppup always has vairant set to 'toolbar' when used in Toolbar
export interface InnerToolbarHelpPopup
  extends CustomToolbarItemProps,
    Omit<HelpPopupProps, 'type' | 'variant'> {
  type: 'HelpPopup';
  buttonRef?: Ref<HTMLButtonElement>;
}

export interface InnerToolbarObjectMarker
  extends CustomToolbarItemProps,
    Omit<ObjectMarkerProps, 'type'> {
  type: 'ObjectMarker';
  objectMarkerType: ObjectMarkerProps['type'];
  ref?: Ref<HTMLDivElement>;
}

export interface InnerToolbarSplitButton
  extends CustomToolbarItemProps,
    Omit<SplitButtonProps, 'type'> {
  type: 'SplitButton';
  ref?: Ref<HTMLDivElement>;
}

export interface InnerToolbarUndoRedo extends CustomToolbarItemProps, Omit<UndoRedoProps, 'type'> {
  type: 'UndoRedo';
}

export interface InnerToolbarObjectSwitcher
  extends CustomToolbarItemProps,
    Omit<ObjectSwitcherProps, 'type'> {
  type: 'ObjectSwitcher';
  ref?: Ref<HTMLButtonElement>;
}

export type InnerToolbarItem =
  | InnerToolbarButton
  | InnerToolbarPredefinedButton
  | InnerToolbarCheckbox
  | InnerToolbarHeading
  | InnerToolbarBadgeButton
  | InnerToolbarMenuButton
  | InnerToolbarSeparator
  | InnerToolbarDropdown
  | InnerToolbarLabel
  | InnerToolbarSegmentedButton
  | InnerToolbarToggleButton
  | InnerToolbarHelpPopup
  | InnerToolbarImage
  | InnerToolbarObjectMarker
  | InnerToolbarSplitButton
  | InnerToolbarSelectWithLabels
  | InnerToolbarBaseMenuButton
  | InnerToolbarUndoRedo
  | InnerToolbarObjectSwitcher;

export const InnerToolbarItemTypes: ReadonlyArray<BaseToolbarItem['type']> = [
  'Button',
  'PredefinedButton',
  'Checkbox',
  'Heading',
  'BadgeButton',
  'MenuButton',
  'BaseMenuButton',
  'ToolbarSeparator',
  'Select',
  'SelectWithLabels',
  'Label',
  'SegmentedButton',
  'ToggleButton',
  'HelpPopup',
  'Image',
  'ObjectMarker',
  'SplitButton',
  'UndoRedo',
  'ObjectSwitcher',
];

export const FocusableItemTypes: ReadonlyArray<BaseToolbarItem['type']> = [
  'Button',
  'PredefinedButton',
  'MenuButton',
  'BaseMenuButton',
  'SegmentedButton',
  'Select',
  'SelectWithLabels',
  'HelpPopup',
  'BadgeButton',
  'ToggleButton',
  'Checkbox',
  'ObjectMarker',
  'SplitButton',
  'UndoRedo',
  'ObjectSwitcher',
];
